/**
 * ClusterService - Frontend API client for cluster management
 * Communicates with Cloudflare Workers backend which proxies to the orchestrator
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://api.tcf-studio.dev';

/**
 * Get authentication token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Create headers with authentication
 */
const getHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * List all clusters for the authenticated user
 * @param {Object} filters - Optional filters (status, cloud_provider)
 * @returns {Promise<Object>} - { success, clusters }
 */
export const listClusters = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.cloudProvider) queryParams.append('cloud_provider', filters.cloudProvider);

    const url = `${API_URL}/clusters${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch clusters');
    }

    return data;
  } catch (error) {
    console.error('List clusters error:', error);
    throw error;
  }
};

/**
 * Quote a cluster shape before launching it.
 *
 * Returns the same rate the biller will charge, so the launch form and the
 * billing cron cannot disagree.
 *
 * @param {Object} shape - { accelerator, acceleratorCount, numNodes, cloudHint,
 *                           regionHint, providerType, useSpot, dockerImage, taskYaml }
 * @returns {Promise<Object>} - { success, estimate, balance_cents, hours_remaining }
 */
export const estimateClusterCost = async (shape) => {
  const response = await fetch(`${API_URL}/clusters/estimate`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(shape)
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to estimate cost');
  }
  return data;
};

/**
 * Get details of a specific cluster
 * @param {number} clusterId - Cluster ID
 * @returns {Promise<Object>} - { success, cluster }
 */
export const getCluster = async (clusterId) => {
  try {
    const response = await fetch(`${API_URL}/clusters/${clusterId}`, {
      method: 'GET',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch cluster');
    }

    return data;
  } catch (error) {
    console.error('Get cluster error:', error);
    throw error;
  }
};

/**
 * Launch a new cluster
 * @param {Object} clusterConfig - Cluster configuration
 * @param {string} clusterConfig.clusterName - Name of the cluster
 * @param {string} clusterConfig.taskYaml - Task YAML configuration
 * @param {string} [clusterConfig.providerType] - Provider type (datacenter/community/both)
 * @param {string} [clusterConfig.regionHint] - Optional region hint
 * @returns {Promise<Object>} - { success, cluster, orchestratorResponse }
 */
export const launchCluster = async (clusterConfig) => {
  try {
    const response = await fetch(`${API_URL}/clusters/launch`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(clusterConfig)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to launch cluster');
    }

    return data;
  } catch (error) {
    console.error('Launch cluster error:', error);
    throw error;
  }
};

/**
 * Get status of all user clusters from orchestrator
 * @returns {Promise<Object>} - { success, clusters }
 */
export const getClusterStatus = async () => {
  try {
    const response = await fetch(`${API_URL}/clusters/status`, {
      method: 'GET',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch cluster status');
    }

    return data;
  } catch (error) {
    console.error('Get cluster status error:', error);
    throw error;
  }
};

/**
 * Stop a running cluster (preserves data)
 * @param {number} clusterId - Cluster ID
 * @returns {Promise<Object>} - { success, cluster, orchestratorResponse }
 */
export const stopCluster = async (clusterId) => {
  try {
    const response = await fetch(`${API_URL}/clusters/${clusterId}/stop`, {
      method: 'POST',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to stop cluster');
    }

    return data;
  } catch (error) {
    console.error('Stop cluster error:', error);
    throw error;
  }
};

/**
 * Start a stopped cluster
 * @param {number} clusterId - Cluster ID
 * @returns {Promise<Object>} - { success, cluster, orchestratorResponse }
 */
export const startCluster = async (clusterId) => {
  try {
    const response = await fetch(`${API_URL}/clusters/${clusterId}/start`, {
      method: 'POST',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to start cluster');
    }

    return data;
  } catch (error) {
    console.error('Start cluster error:', error);
    throw error;
  }
};

/**
 * Tear down a cluster completely (destroys all resources)
 * @param {number} clusterId - Cluster ID
 * @returns {Promise<Object>} - { success, message, orchestratorResponse }
 */
export const downCluster = async (clusterId) => {
  try {
    const response = await fetch(`${API_URL}/clusters/${clusterId}/down`, {
      method: 'POST',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to tear down cluster');
    }

    return data;
  } catch (error) {
    console.error('Down cluster error:', error);
    throw error;
  }
};

/**
 * Gracefully tear down a cluster: the relay stops routing new requests to it
 * and it is torn down automatically once the in-flight queue drains (or after
 * an optional timeout).
 */
export const gracefulDownCluster = async (clusterId, { timeoutS } = {}) => {
  try {
    const response = await fetch(`${API_URL}/clusters/${clusterId}/graceful-down`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(timeoutS != null ? { timeout_s: timeoutS } : {})
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to start graceful teardown');
    }

    return data;
  } catch (error) {
    console.error('Graceful down cluster error:', error);
    throw error;
  }
};

/**
 * Set cluster visibility
 */
export const setClusterVisibility = async (clusterId, visibility, publicPermissions) => {
  const response = await fetch(`${API_URL}/clusters/${clusterId}/visibility`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ visibility, publicPermissions })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to set visibility');
  return data;
};

/**
 * Get cluster grants
 */
export const getClusterGrants = async (clusterId) => {
  const response = await fetch(`${API_URL}/clusters/${clusterId}/grants`, {
    method: 'GET',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch grants');
  return data;
};

/**
 * Add cluster grant
 */
export const addClusterGrant = async (clusterId, userId, permissions) => {
  const response = await fetch(`${API_URL}/clusters/${clusterId}/grants`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ userId, permissions })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to add grant');
  return data;
};

/**
 * Remove cluster grant
 */
export const removeClusterGrant = async (clusterId, userId) => {
  const response = await fetch(`${API_URL}/clusters/${clusterId}/grants/${userId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to remove grant');
  return data;
};

/**
 * Generate sample task YAML configuration
 * @param {Object} params - Configuration parameters
 * @returns {string} - YAML string
 */
export const generateTaskYaml = (params) => {
  const {
    providerType = 'both',
    region,
    minCpuCores = 4,
    minRamGb = 8,
    instanceType = 'both',
    accelerators,
    numNodes = 1,
    diskSize = 100,
    setup = '',
    run = '',
    workdir = '.'
  } = params;

  let yaml = `# Task Configuration
resources:
  cpus: "${minCpuCores}+"
  memory: "${minRamGb}+"
`;

  if (instanceType === 'spot') yaml += `  use_spot: true\n`;
  else if (instanceType === 'on-demand') yaml += `  use_spot: false\n`;

  if (region) yaml += `  region: ${region}\n`;
  if (accelerators) yaml += `  accelerators: ${accelerators}\n`;
  yaml += `  disk_size: ${diskSize}\n`;

  if (numNodes > 1) {
    yaml += `\nnum_nodes: ${numNodes}\n`;
  }

  if (setup) {
    yaml += `\nsetup: |\n`;
    setup.split('\n').forEach(line => {
      yaml += `  ${line}\n`;
    });
  }

  if (run) {
    yaml += `\nrun: |\n`;
    run.split('\n').forEach(line => {
      yaml += `  ${line}\n`;
    });
  }

  yaml += `\nworkdir: ${workdir}\n`;

  return yaml;
};

/**
 * Parse YAML configuration to extract resource details
 * @param {string} yamlString - YAML configuration string
 * @returns {Object} - Parsed configuration object
 */
export const parseTaskYaml = (yamlString) => {
  const config = {};

  try {
    const cloudMatch = yamlString.match(/cloud:\s*(\w+)/i);
    if (cloudMatch) config.cloud = cloudMatch[1];

    const regionMatch = yamlString.match(/region:\s*([^\n]+)/i);
    if (regionMatch) config.region = regionMatch[1].trim();

    const instanceMatch = yamlString.match(/instance_type:\s*([^\n]+)/i);
    if (instanceMatch) config.instanceType = instanceMatch[1].trim();

    const acceleratorMatch = yamlString.match(/accelerators:\s*\{?\s*["']?([A-Za-z][^:"'\}\n]*[A-Za-z0-9])["']?\s*(?::\s*["']?(\d+))?/i);
    if (acceleratorMatch) {
      config.acceleratorType = acceleratorMatch[1];
      config.acceleratorCount = parseInt(acceleratorMatch[2] || '1');
    }

    const numNodesMatch = yamlString.match(/num_nodes:\s*(\d+)/i);
    if (numNodesMatch) config.numNodes = parseInt(numNodesMatch[1]);

    const diskSizeMatch = yamlString.match(/disk_size:\s*(\d+)/i);
    if (diskSizeMatch) config.diskSize = parseInt(diskSizeMatch[1]);

    const autostopMatch = yamlString.match(/autostop:\s*(\d+)/i);
    if (autostopMatch) config.autostop = parseInt(autostopMatch[1]);

  } catch (err) {
    console.error('Error parsing YAML:', err);
  }

  return config;
};

/**
 * Get status badge color based on cluster status
 * @param {string} status - Cluster status
 * @returns {string} - Tailwind color class
 */
export const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case 'UP':
      return 'bg-green-500';
    case 'INIT':
      return 'bg-yellow-500';
    case 'STOPPED':
      return 'bg-gray-500';
    case 'DRAINING':
    case 'TERMINATING':
      return 'bg-amber-500';
    case 'DOWN':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
};

/**
 * Get status text color based on cluster status
 * @param {string} status - Cluster status
 * @returns {string} - Tailwind color class
 */
export const getStatusTextColor = (status) => {
  switch (status?.toUpperCase()) {
    case 'UP':
      return 'text-green-600';
    case 'INIT':
      return 'text-yellow-600';
    case 'STOPPED':
      return 'text-gray-600';
    case 'DRAINING':
    case 'TERMINATING':
      return 'text-amber-600';
    case 'DOWN':
      return 'text-red-600';
    default:
      return 'text-gray-500';
  }
};

/**
 * Format timestamp to readable date
 * @param {string} timestamp - ISO timestamp
 * @returns {string} - Formatted date
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  return date.toLocaleString();
};

/**
 * Get detailed logs/progress for a cluster
 * @param {number} clusterId - Cluster ID
 * @returns {Promise<Object>} - Cluster details including orchestrator logs
 */
export const getClusterLogs = async (clusterId) => {
  try {
    const response = await fetch(`${API_URL}/clusters/${clusterId}/logs`, {
      method: 'GET',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch cluster logs');
    }

    return data;
  } catch (error) {
    console.error('Get cluster logs error:', error);
    throw error;
  }
};

/**
 * Get runtime application logs for a running cluster
 * @param {number} clusterId - Cluster ID
 * @param {Object} options
 * @param {number} [options.tail] - Number of lines
 * @param {boolean} [options.full] - Full history
 * @param {number} [options.nodeIndex] - Specific node index (omit for head/default)
 * @returns {Promise<Object>} - { success, logs, job_id, cluster_name }
 */
export const getClusterRuntimeLogs = async (clusterId, { tail, full, nodeIndex, jobId } = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (tail) searchParams.set('tail', tail);
    if (full) searchParams.set('full', 'true');
    if (nodeIndex !== undefined && nodeIndex !== null) searchParams.set('node_index', nodeIndex);
    if (jobId !== undefined && jobId !== null) searchParams.set('job_id', jobId);
    const params = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await fetch(`${API_URL}/clusters/${clusterId}/runtime-logs${params}`, {
      method: 'GET',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch runtime logs');
    }

    return data;
  } catch (error) {
    console.error('Get cluster runtime logs error:', error);
    throw error;
  }
};

/**
 * Get live resource metrics for a running cluster
 * @param {number} clusterId - Cluster ID
 * @param {Object} [options]
 * @param {number} [options.nodeIndex] - Specific node index (omit for all nodes)
 * @returns {Promise<Object>} - { success, metrics, cluster_name }
 */
/**
 * Run one shell command on a cluster node. Admin only.
 *
 * A non-zero exit code comes back as a successful response carrying
 * `returncode` and `stderr` — the command ran, it just failed. Only a
 * transport or authorization problem throws.
 */
export const execClusterCommand = async (clusterId, command, { nodeIndex, timeout } = {}) => {
  const response = await fetch(`${API_URL}/clusters/${clusterId}/exec`, {
    method: 'POST',
    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ command, nodeIndex, timeout }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `Command failed (${response.status})`);
  }
  return data;
};

export const getClusterMetrics = async (clusterId, { nodeIndex } = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (nodeIndex !== undefined && nodeIndex !== null) searchParams.set('node_index', nodeIndex);
    const params = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await fetch(`${API_URL}/clusters/${clusterId}/metrics${params}`, {
      method: 'GET',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch metrics');
    }

    return data;
  } catch (error) {
    console.error('Get cluster metrics error:', error);
    throw error;
  }
};

/**
 * Get resolved frp tunnel endpoints exposed by a cluster.
 * @returns {Promise<Object>} - { success, tunnels: [{name, protocol, local_port, url|endpoint}], tunnel_id }
 */
export const getClusterTunnels = async (clusterId) => {
  try {
    const response = await fetch(`${API_URL}/clusters/${clusterId}/tunnels`, {
      method: 'GET',
      headers: getHeaders()
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to fetch tunnels');
    return data;
  } catch (error) {
    console.error('Get cluster tunnels error:', error);
    throw error;
  }
};

/**
 * Get the list of nodes in a cluster
 * @param {number} clusterId - Cluster ID
 * @returns {Promise<Object>} - { success, nodes, num_nodes, cluster_name }
 */
export const getClusterNodes = async (clusterId) => {
  try {
    const response = await fetch(`${API_URL}/clusters/${clusterId}/nodes`, {
      method: 'GET',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch nodes');
    }

    return data;
  } catch (error) {
    console.error('Get cluster nodes error:', error);
    throw error;
  }
};

export const updateClusterPricing = async (clusterId, pricingRates) => {
  const response = await fetch(`${API_URL}/clusters/${clusterId}/pricing`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ pricingRates })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update pricing');
  return data;
};

export default {
  listClusters,
  getCluster,
  launchCluster,
  estimateClusterCost,
  getClusterStatus,
  getClusterLogs,
  getClusterRuntimeLogs,
  getClusterMetrics,
  getClusterTunnels,
  execClusterCommand,
  getClusterNodes,
  stopCluster,
  startCluster,
  downCluster,
  gracefulDownCluster,
  setClusterVisibility,
  getClusterGrants,
  addClusterGrant,
  removeClusterGrant,
  updateClusterPricing,
  generateTaskYaml,
  parseTaskYaml,
  getStatusColor,
  getStatusTextColor,
  formatTimestamp
};
